<template>
  <div class="app-container">
    <Container
      :title="t('approvalForm.approvalFormDetail')"
      :hasLine="true"
    >
      <template #main>
        <DynamicCollapseForApproval
          ref="dynamicCollapseRef"
          v-model:groups="groupedList"
          :columnStruct="getColumnStruct(approvalForm)"
          :listLoading="listLoading"
          hadOperate
        >
          <template
            #default="slotProps"
          >
            <el-button
              @click="viewDetail(slotProps.row)"
              type="primary"
              size="mini"
            >
              {{ t('Acceptance.viewDetails') }}
            </el-button>
          </template>
          <template #slot-paymentDocuments="{row}">
            <el-link
              type="primary"
              :href="row.paymentDocuments"
              @click.prevent="handleClickLink(row, $event)"
            >
              {{ row.paymentDocuments }}
            </el-link>
          </template>
        </DynamicCollapseForApproval>
      </template>
    </Container>
  </div>
  <el-dialog
    v-model="detailDialogVisible"
    width="90%"
    destroy-on-close
    :title="t('approvalForm.approvalFormDetail')"
  >
    <div style="height: 50vh">
      <SimpleTable
        ref="tableRef"
        :listLoading="visibleListLoading"
        :tableData="approvalVisibleData"
        :columnStruct="detailStruct"
        :hadSelect="false"
        :had-operate="false"
      />
    </div>
    <div
      class="dialog-footer"
      style="text-align: right;"
    >
      <el-button @click="detailDialogVisible = false">
        {{ t('tagsView.close') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, reactive, toRefs, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { formatDate, formatThousands } from '@/utils'
import {
  getApprovalFormDetailById,
  getApprovalHistoryDetailById
} from '@/apis/process/approval'
import Container from '@/components/form/container/Container.vue'
import DynamicCollapseForApproval from '@/components/collapse/DynamicCollapseForApproval.vue'
import {
  ApprovalFormModel, ApprovalFormGroupModel, ApprovalPaymentHistoryModel
} from '@/model/process/approvalModel'
import { GroupModel } from '@/components/collapse/CollapseGroupInterface'
import Decimal from 'decimal.js'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import { intelligentClipboard } from '@/utils/clipboard'

export default defineComponent({
  components: {
    SimpleTable,
    DynamicCollapseForApproval,
    Container
  },
  setup() {
    const { t } = useI18n()
    const id = useRoute().params.id.toString()
    const dynamicCollapseRef = ref()
    const formatStatus = (status: number | null) => {
      if (status === 8) {
        return t('common.rejected')
      } else if (status === 9) {
        return t('common.canceled')
      } else if (status === 4) {
        return t('common.hasApproved')
      }
      return null
    }
    const formatPayment = (group: ApprovalFormGroupModel, paymentType: number | null) => {
      if (paymentType === 1) {
        return group.approvalPaymentSap ?? []
      } else if (paymentType === 2) {
        return group.approvalPaymentPo ?? []
      } else if (paymentType === 3) {
        return group.approvalPaymentWithoutPo ?? []
      } else if (paymentType === 4) {
        return group.approvalReimbursement ?? []
      } else if (paymentType === 5) {
        return group.approvalPaymentRefund ?? []
      }
      return []
    }
    const formatAmountName = (status: number | null) => {
      if (status && status === 4) {
        return t('bank.amountApproved')
      } else {
        return t('bank.totalAmount')
      }
    }
    const paymentSapStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'documentNumber', label: t('approvalForm.documentNumber') },
      { prop: 'reference', label: t('approvalForm.reference') },
      { prop: 'amount', label: t('approvalForm.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('approvalForm.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'text', label: t('approvalForm.text') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      {
        prop: 'approver',
        label: t('approvalForm.finalApprover')
      },
      {
        prop: 'status',
        label: t('table.status'),
        formatter: (row: any) => formatStatus(row.status),
        icon: 'warning',
        showIcon: (row: any) => row.status === 8,
        iconTooltip: 'comments'
      }
    ]
    const paymentPoStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType') },
      { prop: 'reference', label: t('approvalForm.reference') },
      { prop: 'amount', label: t('approvalForm.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), slot: true },
      {
        prop: 'approver',
        label: t('approvalForm.finalApprover')
      },
      {
        prop: 'status',
        label: t('table.status'),
        formatter: (row: any) => formatStatus(row.status),
        icon: 'warning',
        showIcon: (row: any) => row.status === 8,
        iconTooltip: 'comments'
      }
    ]
    const paymentWithoutPoStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType') },
      { prop: 'reference', label: t('approvalForm.reference') },
      { prop: 'amount', label: t('approvalForm.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), slot: true },
      {
        prop: 'approver',
        label: t('approvalForm.finalApprover')
      },
      {
        prop: 'status',
        label: t('table.status'),
        formatter: (row: any) => formatStatus(row.status),
        icon: 'warning',
        showIcon: (row: any) => row.status === 8,
        iconTooltip: 'comments'
      }
    ]
    const reimbursementStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'amount', label: t('approvalForm.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      {
        prop: 'approver',
        label: t('approvalForm.finalApprover')
      },
      {
        prop: 'status',
        label: t('table.status'),
        formatter: (row: any) => formatStatus(row.status),
        icon: 'warning',
        showIcon: (row: any) => row.status === 8,
        iconTooltip: 'comments'
      }
    ]
    const paymentRefundStruct = [
      { prop: 'id', label: t('approvalForm.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType') },
      { prop: 'reference', label: t('approvalForm.reference') },
      { prop: 'amount', label: t('approvalForm.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('approvalForm.currency') },
      {
        prop: 'paymentDate',
        label: t('approvalForm.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'comment', label: t('approvalForm.comment') },
      { prop: 'purpose', label: t('approvalForm.purpose') },
      { prop: 'paymentDocuments', label: t('order.paymentDocuments'), slot: true },
      {
        prop: 'approver',
        label: t('approvalForm.finalApprover')
      },
      {
        prop: 'status',
        label: t('table.status'),
        formatter: (row: any) => formatStatus(row.status),
        icon: 'warning',
        showIcon: (row: any) => row.status === 8,
        iconTooltip: 'comments'
      }
    ]
    const detailColumnStruct = [
      {
        prop: 'approvalLevel',
        label: t('approvalForm.approvalProcess'),
        formatter: (row: ApprovalPaymentHistoryModel) => {
          switch (row.approvalLevel) {
            case 1:
              return t('process.level1Approver')
            case 2:
              return t('process.level2Approver')
            case 3:
              return t('process.level3Approver')
            default:
              return ''
          }
        }
      },
      { prop: 'approverName', label: t('approvalForm.approver') },
      {
        prop: 'approvalResult',
        label: t('approvalForm.approvalResult'),
        formatter: (row: ApprovalPaymentHistoryModel) => {
          return row.approvalResult ? t('approvalForm.approved') : t('approvalForm.rejected')
        }
      },
      { prop: 'comments', label: t('approvalForm.rejectComment') },
      {
        prop: 'createdAt',
        label: t('approvalForm.operateDate'),
        formatter: (row: ApprovalPaymentHistoryModel) => formatDate(row.createdAt)
      }
    ]
    const dataMap = reactive({
      groupedList: Array<GroupModel>(),
      approvalForm: {} as ApprovalFormModel | null,
      listLoading: true,
      detailDialogVisible: false,
      approvalVisibleData: Array<ApprovalPaymentHistoryModel>(),
      detailStruct: detailColumnStruct,
      visibleListLoading: false,
      async getDetailList() {
        dataMap.listLoading = true
        const data = await getApprovalFormDetailById(id)
        if (data && data.data) {
          dataMap.approvalForm = data.data.approvalForm
          if (data.data.approvalFormGroup && data.data.approvalFormGroup.length > 0) {
            dataMap.groupedList = data.data.approvalFormGroup.map(
              (item) => ({
                key: item.code,
                name: item.name,
                accountNumber: null,
                currency: item.currency,
                paymentDate: item.paymentDate ? formatDate(item.paymentDate, 'yyyy-MM-dd') : null,
                totalAmount: new Decimal(item.totalAmount || 0).toString(),
                totalAmountName: formatAmountName(dataMap.approvalForm?.status ?? null),
                tableList: formatPayment(item, dataMap.approvalForm?.paymentType ?? null)
              })
            )
          }
        } else {
          dataMap.groupedList = []
        }
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      async viewDetail(row: any) {
        dataMap.visibleListLoading = true
        const data = await getApprovalHistoryDetailById(row.id)
        if (data && data.data) {
          dataMap.approvalVisibleData = data.data
        } else {
          dataMap.approvalVisibleData = []
        }
        dataMap.detailDialogVisible = true
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.visibleListLoading = false
        }, 0.5 * 1000)
      },
      async handleClickLink(row: any, event: MouseEvent) {
        await intelligentClipboard(row.paymentDocuments, event)
      }
    })

    const getColumnStruct = (approvalForm: ApprovalFormModel | null) => {
      let columnStruct: any[] = []
      if (approvalForm) {
        switch (approvalForm.paymentType) {
          case 1:
            columnStruct = Object.assign([], paymentSapStruct)
            break
          case 2:
            columnStruct = Object.assign([], paymentPoStruct)
            break
          case 3:
            columnStruct = Object.assign([], paymentWithoutPoStruct)
            break
          case 4:
            columnStruct = Object.assign([], reimbursementStruct)
            break
          case 5:
            columnStruct = Object.assign([], paymentRefundStruct)
            break
        }
      }
      return columnStruct
    }
    onMounted(() => {
      dataMap.getDetailList()
    })
    return {
      t,
      dynamicCollapseRef,
      getColumnStruct,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
